import React, { useState } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Link,
  Divider,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Security,
  Email,
  Lock,
  Person,
} from '@mui/icons-material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import DomainRequestDialog from '../components/DomainRequestDialog';
import ErrorDialog from '../components/ErrorDialog';
import SuccessDialog from '../components/SuccessDialog';

const Register: React.FC = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [errorCode, setErrorCode] = useState('');
  const [showDomainRequestDialog, setShowDomainRequestDialog] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorDialogKey, setErrorDialogKey] = useState(0);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [successEmail, setSuccessEmail] = useState('');

  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
    setErrorCode('');
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setErrorCode('');

    // Validaciones
    if (formData.password !== formData.confirmPassword) {
      setError('Las contraseñas no coinciden');
      setLoading(false);
      return;
    }

    if (formData.password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      setLoading(false);
      return;
    }

    try {
      const response = await register({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        password: formData.password
      });
      
      // Mostrar mensaje de éxito y redirigir al login
      setError('');
      setErrorCode('');
      setShowSuccess(true);
      setSuccessMessage('Usuario registrado exitosamente. Por favor verifica tu email para activar tu cuenta.');
      setSuccessEmail(formData.email);
      
      // Limpiar formulario
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        confirmPassword: ''
      });
      
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Error al registrarse';
      const errorCode = err.response?.data?.code || '';
      
      setError(errorMessage);
      setErrorCode(errorCode);
      
      // Manejar diferentes tipos de errores
      if (errorCode === 'PUBLIC_EMAIL_NOT_ALLOWED') {
        // Para correos públicos, mostrar ErrorDialog
        setShowError(true);
        setErrorDialogKey(prev => prev + 1);
      } else if (errorCode === 'DOMAIN_NOT_AUTHORIZED') {
        // Para dominios empresariales, mostrar DomainRequestDialog
        setShowDomainRequestDialog(true);
      } else {
        // Para otros errores, mostrar Alert (ya manejado en el render)
        setShowError(false);
      }
      
      console.error('Error en registro:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleToggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        p: 2,
      }}
    >
      <Paper
        elevation={24}
        sx={{
          p: 4,
          maxWidth: 600,
          width: '100%',
          borderRadius: 3,
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
        }}
      >
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Security sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
          <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
            🛡️ Vigilantium
          </Typography>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Portal de E-Learning
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Crea tu cuenta para acceder al portal de ciberseguridad
          </Typography>
        </Box>

        {error && errorCode !== 'DOMAIN_NOT_AUTHORIZED' && errorCode !== 'PUBLIC_EMAIL_NOT_ALLOWED' && (
          <Alert 
            severity="error" 
            sx={{ mb: 3 }}
          >
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit} style={{ marginTop: '8px' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Nombre y Apellido */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                required
                sx={{ flex: 1, minWidth: '200px' }}
                id="firstName"
                label="Nombre"
                name="firstName"
                autoComplete="given-name"
                value={formData.firstName}
                onChange={handleChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person color="action" />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                required
                sx={{ flex: 1, minWidth: '200px' }}
                id="lastName"
                label="Apellido"
                name="lastName"
                autoComplete="family-name"
                value={formData.lastName}
                onChange={handleChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {/* Email */}
            <TextField
              required
              fullWidth
              id="email"
              label="Correo electrónico"
              name="email"
              autoComplete="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email color="action" />
                  </InputAdornment>
                ),
              }}
            />

            {/* Contraseñas */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                required
                sx={{ flex: 1, minWidth: '200px' }}
                name="password"
                label="Contraseña"
                type={showPassword ? 'text' : 'password'}
                id="password"
                autoComplete="new-password"
                value={formData.password}
                onChange={handleChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleTogglePasswordVisibility}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                required
                sx={{ flex: 1, minWidth: '200px' }}
                name="confirmPassword"
                label="Confirmar Contraseña"
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirmPassword"
                autoComplete="new-password"
                value={formData.confirmPassword}
                onChange={handleChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle confirm password visibility"
                        onClick={handleToggleConfirmPasswordVisibility}
                        edge="end"
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

          </Box>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={loading}
            sx={{
              mt: 3,
              mb: 2,
              py: 1.5,
              fontSize: '1.1rem',
              fontWeight: 'bold',
              borderRadius: 2,
              background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
              '&:hover': {
                background: 'linear-gradient(45deg, #5a6fd8 30%, #6a4190 90%)',
              },
            }}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Registrarse'
            )}
          </Button>

          <Divider sx={{ my: 3 }}>
            <Typography variant="body2" color="text.secondary">
              ¿Ya tienes cuenta?
            </Typography>
          </Divider>

          <Button
            fullWidth
            variant="outlined"
            size="large"
            component={RouterLink}
            to="/login"
            sx={{
              py: 1.5,
              fontSize: '1.1rem',
              borderRadius: 2,
              borderColor: 'primary.main',
              color: 'primary.main',
              '&:hover': {
                borderColor: 'primary.dark',
                backgroundColor: 'primary.main',
                color: 'white',
              },
            }}
          >
            Iniciar Sesión
          </Button>
        </form>

        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            Al registrarte, aceptas nuestros{' '}
            <Link href="#" color="primary">
              Términos de Servicio
            </Link>{' '}
            y{' '}
            <Link href="#" color="primary">
              Política de Privacidad
            </Link>
          </Typography>
        </Box>
      </Paper>

      {/* Ventana flotante para solicitud de dominio */}
      <DomainRequestDialog
        open={showDomainRequestDialog}
        onClose={() => {
          setShowDomainRequestDialog(false);
          setError('');
          setErrorCode('');
        }}
        userData={{
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email
        }}
        domain={formData.email.split('@')[1] || ''}
      />

      {/* Error Dialog para correos públicos */}
      <ErrorDialog
        key={errorDialogKey}
        open={showError && !!error}
        onClose={() => {
          setShowError(false);
          setError('');
          setErrorCode('');
          setErrorDialogKey(prev => prev + 1);
        }}
        errorCode={errorCode}
        errorMessage={error}
      />

      {/* Success Dialog */}
      <SuccessDialog
        open={showSuccess}
        onClose={() => {
          setShowSuccess(false);
          navigate('/login');
        }}
        message={successMessage}
        email={successEmail}
      />
    </Box>
  );
};

export default Register;
